import React, { useEffect } from "react";

import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { AD_BREAK, MEDIA_FORMATS } from "../../../constants/media";
import useFAST from "../../../contexts/FASTContext";
import { getTranslatedMetadata } from "../../../utils/data/Content";
import { getMediaFormatsWithAdBreak } from "../../../utils/fast";
import Select from "../../form/Select";

import type { JSX } from "react";
import type { StylesConfig } from "react-select";

import type { FormatFilter } from "../../../api/MediaAPI.types";
import type {
  MediaFormat,
  MediaFormatWithAdBreak,
} from "../../../constants/media";
import type { AppState } from "../../../reducers";
import type { KeyWithTranslation } from "../../../types";

interface Props {
  onChange: (value: MediaFormatWithAdBreak[]) => void;
  defaultFormatFilter?: FormatFilter;
  mediaFormatList?: KeyWithTranslation<MediaFormat>[];
  isLoading: boolean;
}

interface FilterOption {
  value: MediaFormatWithAdBreak;
  label: string;
  color: string;
}

interface FieldValue {
  formatFilters: FilterOption[];
}

export default function MediaLibraryFilters({
  onChange,
  defaultFormatFilter,
  mediaFormatList,
  isLoading,
}: Props): JSX.Element {
  const methods = useForm<FieldValue>();
  const { state: fastState } = useFAST();
  const { t } = useTranslation("editor-fast-channel");
  const results = useWatch({ control: methods.control, name: "formatFilters" });

  const languageId = useSelector(
    (state: AppState) => state.languages.current?.uuid,
  );

  useEffect(() => {
    const filterList = results?.map((result) => result.value);
    onChange(filterList || []);
  }, [onChange, results]);

  const formatColors = fastState.channelData?.medias_colors;

  const getFilterOptions = (): {
    label: string;
    value: string;
    color: string;
  }[] => {
    if (formatColors && mediaFormatList) {
      const isExcludes =
        defaultFormatFilter &&
        "excludes" in defaultFormatFilter &&
        defaultFormatFilter.excludes;

      const isIncludes =
        defaultFormatFilter &&
        "includes" in defaultFormatFilter &&
        defaultFormatFilter.includes;

      const formats = getMediaFormatsWithAdBreak(mediaFormatList);

      const formatOptions = formats.reduce((acc: FilterOption[], format) => {
        if (isExcludes) {
          if (defaultFormatFilter.excludes?.includes(format.key)) return acc;
        }

        if (isIncludes) {
          if (!defaultFormatFilter.includes?.includes(format.key)) return acc;
        }

        if (format.key === AD_BREAK) {
          acc.push({
            value: AD_BREAK,
            label: t("ad_break"),
            color: formatColors[MEDIA_FORMATS.ADPOD],
          });
        } else {
          acc.push({
            value: format.key,
            label: getTranslatedMetadata(format, "title", languageId) as string,
            color: formatColors[format.key],
          });
        }

        return acc;
      }, []);

      return formatOptions;
    }

    return [];
  };

  const colourStyles: StylesConfig<FilterOption, true> = {
    control: (styles) => ({
      ...styles,
      backgroundColor: "var(--card)",
    }),
    option: (styles, { data }) => ({
      ...styles,
      display: "flex",
      gap: 10,

      ":before": {
        content: '" "',
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: data.color,
        alignSelf: "center",
      },
    }),
    multiValue: (styles, { data }) => ({
      ...styles,
      ":before": {
        content: '" "',
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: data.color,
        alignSelf: "center",
      },
    }),
  };

  return (
    <FormProvider
      {...methods} // eslint-disable-line react/jsx-props-no-spreading
    >
      <Select
        isCompact
        isMulti
        className="mb-[0.3125rem]!"
        isLoading={isLoading}
        name="formatFilters"
        options={getFilterOptions()}
        placeholder={t("filterByFormat")}
        styles={colourStyles}
      />
    </FormProvider>
  );
}
